import * as names from "@/store/names";

// Vistas que forman parte del contexto de idioma de asignatura.
// Mientras la navegación ocurra entre estas vistas, el idioma no se revierte.
const MATTER_LANGUAGE_VIEWS = [
  "MatterProgramView",
  // E53 · El Diseño Instruccional y su documento de salida, en los dos niveles. Son
  // contenido de la asignatura igual que el Programa y las Evaluaciones: si está escrita
  // en otro idioma, se lee en ese idioma. Estaban fuera, así que bajar del Programa al
  // Diseño revertía el idioma a mitad de camino.
  "InstructionalView",
  "InstructionalMatterView",
  "InstructionalMatrixView",
  "InstructionalMatrixMatterView",
  "NewEvaluationView",
  "EvaluatorView",
  "EvaluateeView",
  "LearningEvaluationView",
  "LearningExperiencesView",
  "LearningMatterDescription",
  "LearningSuitcaseStudentView",
  "LearningSuitcaseTeacherView",
  "LearningSuitcaseView",
];

export default {
  methods: {
    /**
     * Aplica el idioma de la asignatura si es distinto al idioma global del usuario.
     * Guarda el estado original en Vuex (no en localStorage/sessionStorage)
     * para que cada pestaña sea independiente.
     *
     * @param {number|null} defaultLanguageId - matter.default_language
     */
    async applyMatterLanguageById(defaultLanguageId) {
      // Si la asignatura no tiene idioma explícito, no se fuerza ningún cambio:
      // el idioma por defecto de la institución no implica que el contenido de
      // la asignatura esté escrito en ese idioma (ver bug reportado 2026-08-11).
      if (!defaultLanguageId) return;

      const langs = await this.$store.dispatch(names.FETCH_LANGUAGES);

      // Si el usuario navegó fuera mientras cargaban los idiomas, abortar.
      if (this.matterLangAborted) return;

      const lang = langs.find((l) => l.id === defaultLanguageId && l.is_active_matter_view);

      const existingContext = this.$store.getters[names.MATTER_LANGUAGE_CONTEXT];

      if (existingContext) {
        // Ya hay contexto activo (venimos de otra vista del grupo o se cambió el
        // idioma de la asignatura). Solo re-aplicamos si el idioma realmente cambió,
        // pero sin sobreescribir savedLocale/savedAlternativeNames.
        if (!lang) {
          // La nueva asignatura no tiene idioma activo de vista: revertir.
          this.revertMatterLanguage();
          return;
        }
        if (lang.code === this.$i18n.locale) return;
        // Cambio de idioma dentro del contexto activo.
        this.$i18n.locale = lang.code;
        const altNames = await this.$restful.GetAll(
          `/common/alternative-names/?language_id=${lang.id}`
        );
        if (this.$store.getters[names.MATTER_LANGUAGE_CONTEXT]) {
          this.$store.commit(names.OVERRIDE_ALTERNATIVE_NAMES, altNames);
        }
        return;
      }

      if (!lang) return;

      // Leer el idioma global real desde localStorage (la fuente de verdad
      // para la preferencia del usuario, no contaminada por overrides).
      const globalLocale =
        localStorage.getItem("user_language") || this.$i18n.locale;

      // Si el idioma de la asignatura es igual al global, no hay nada que hacer.
      if (lang.code === globalLocale) return;

      // Guardar estado original en Vuex antes de cambiar.
      this.$store.commit(names.SET_MATTER_LANGUAGE_CONTEXT, {
        savedLocale: globalLocale,
        savedAlternativeNames: [
          ...this.$store.getters[names.ALTERNATIVE_NAMES],
        ],
      });

      // Aplicar idioma de la asignatura solo en memoria (i18n es por instancia Vue)
      this.$i18n.locale = lang.code;

      const altNames = await this.$restful.GetAll(
        `/common/alternative-names/?language_id=${lang.id}`
      );

      // Solo aplicar si el contexto sigue activo (no se revirtió durante el await).
      if (this.$store.getters[names.MATTER_LANGUAGE_CONTEXT]) {
        this.$store.commit(names.OVERRIDE_ALTERNATIVE_NAMES, altNames);
      }
    },

    /**
     * Restaura el idioma y los nombres alternativos originales del usuario.
     * Limpia el contexto de Vuex.
     */
    revertMatterLanguage() {
      const context = this.$store.getters[names.MATTER_LANGUAGE_CONTEXT];
      if (!context) return;
      const { savedLocale, savedAlternativeNames } = context;
      this.$i18n.locale = savedLocale;
      this.$store.commit(names.OVERRIDE_ALTERNATIVE_NAMES, savedAlternativeNames);
      this.$store.commit(names.CLEAR_MATTER_LANGUAGE_CONTEXT);
    },

    /**
     * Dado un objeto evaluation (con scope y scope_id), resuelve el
     * default_language de la asignatura relacionada y aplica el idioma.
     *
     * scope 1 → sección → egress_profile_matter → matter
     * scope 2 → egress_profile_matter → matter
     * scope 3 → matter directamente
     */
    async applyLanguageForEvaluation(evaluation) {
      if (!evaluation) return;
      let matterId = null;

      if (evaluation.scope === 1 && evaluation.scope_id != null) {
        const section = await this.$store.dispatch(
          names.FETCH_SECTION,
          evaluation.scope_id
        );
        if (section.egress_profile_matter) {
          const epm = await this.$store.dispatch(
            names.FETCH_EGRESS_PROFILE_MATTER,
            section.egress_profile_matter
          );
          matterId = epm.matter;
        } else if (section.matter) {
          matterId = section.matter;
        }
      } else if (evaluation.scope === 2 && evaluation.scope_id != null) {
        const epm = await this.$store.dispatch(
          names.FETCH_EGRESS_PROFILE_MATTER,
          evaluation.scope_id
        );
        matterId = epm.matter;
      } else if (evaluation.scope === 3 && evaluation.scope_id != null) {
        matterId = evaluation.scope_id;
      }

      if (matterId) {
        const matter = await this.$store.dispatch(names.FETCH_MATTER, matterId);
        await this.applyMatterLanguageById(matter.default_language);
      }
    },
  },

  beforeRouteLeave(to, _from, next) {
    // Cancela cualquier applyMatterLanguageById que esté en vuelo.
    this.matterLangAborted = true;
    if (!MATTER_LANGUAGE_VIEWS.includes(to.name)) {
      this.revertMatterLanguage();
    }
    next();
  },
};
